import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const BxgyDiscountGenerator = () => {
  const [code, setCode] = useState('3PEVEO');
  const [pwoGid, setPwoGid] = useState('');
  const [shakerGid, setShakerGid] = useState('');
  const [chalkGid, setChalkGid] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCreateBxgy = async () => {
    if (!code.trim()) {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Skriv inn en rabattkode",
      });
      return;
    }

    if (!pwoGid.trim() || !shakerGid.trim() || !chalkGid.trim()) {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Alle produkt-GIDer må fylles ut",
      });
      return;
    }

    setIsCreating(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-shopify-bxgy', {
        body: {
          code: code.trim().toUpperCase(),
          buyProductIds: [pwoGid.trim()],
          buyQuantity: 3,
          getProductIds: [shakerGid.trim(), chalkGid.trim()],
          getQuantity: 2, // 1 shaker + 1 chalk = 2 items
        },
      });

      if (error) throw error;

      if (data.success) {
        setCreatedCode(code.trim().toUpperCase());
        toast({
          title: "Suksess! 🎉",
          description: data.message || "BXGY rabattkode opprettet!",
        });
      } else {
        throw new Error(data.error || 'Ukjent feil');
      }
    } catch (error: any) {
      console.error('Error creating BXGY discount:', error);
      toast({
        variant: "destructive",
        title: "Feil ved oppretting av rabattkode",
        description: error.message || "Noe gikk galt. Prøv igjen.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(createdCode);
      setCopied(true);
      toast({
        title: "Kopiert! 📋",
        description: `Koden "${createdCode}" er kopiert til utklippstavlen`,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Kunne ikke kopiere",
        description: "Prøv å kopiere manuelt",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Opprett "Kjøp 3 få 2 gratis" Rabattkode</CardTitle>
        <CardDescription>
          Kjøp 3 PWO, få 1 Shaker + 1 Chalk gratis
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            <p className="font-semibold mb-2">📝 Slik finner du Produkt-GIDer i Shopify:</p>
            <ol className="text-sm space-y-1 list-decimal list-inside">
              <li>Gå til Shopify Admin → Products</li>
              <li>Klikk på produktet (f.eks. "Peveo Competition PWO")</li>
              <li>Se i URL-en: <code className="bg-muted px-1 rounded">...admin/products/[NUMMER]</code></li>
              <li>GID er: <code className="bg-muted px-1 rounded">gid://shopify/Product/[NUMMER]</code></li>
            </ol>
            <Button
              variant="link"
              className="p-0 h-auto mt-2"
              onClick={() => window.open('https://6pjebd-jw.myshopify.com/admin/products', '_blank')}
            >
              Åpne Shopify Products <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="bxgy-code">Rabattkode</Label>
          <Input
            id="bxgy-code"
            placeholder="3PEVEO"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pwo-gid">PWO Produkt-GID</Label>
          <Input
            id="pwo-gid"
            placeholder="gid://shopify/Product/9912062910773"
            value={pwoGid}
            onChange={(e) => setPwoGid(e.target.value)}
            className="font-mono text-xs"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="shaker-gid">Shaker Produkt-GID</Label>
          <Input
            id="shaker-gid"
            placeholder="gid://shopify/Product/9912059502005"
            value={shakerGid}
            onChange={(e) => setShakerGid(e.target.value)}
            className="font-mono text-xs"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="chalk-gid">Chalk Produkt-GID</Label>
          <Input
            id="chalk-gid"
            placeholder="gid://shopify/Product/9912060977461"
            value={chalkGid}
            onChange={(e) => setChalkGid(e.target.value)}
            className="font-mono text-xs"
          />
        </div>

        <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
          <p><strong>📦 Kunden kjøper:</strong> 3 × Peveo Competition PWO-Stim-Free</p>
          <p><strong>🎁 Kunden får gratis:</strong> 1 × Shaker + 1 × Chalk</p>
          <p><strong>🔢 Bruksgrense:</strong> 1 gang per ordre</p>
        </div>

        <Button 
          onClick={handleCreateBxgy} 
          disabled={isCreating || !code.trim() || !pwoGid.trim() || !shakerGid.trim() || !chalkGid.trim()}
          className="w-full"
          size="lg"
        >
          {isCreating ? 'Oppretter...' : 'Opprett BXGY Rabattkode'}
        </Button>

        {createdCode && (
          <div className="p-4 bg-success/10 border border-success rounded-lg space-y-3">
            <p className="text-sm font-medium text-success">
              ✅ Rabattkode opprettet!
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-background rounded border text-lg font-bold text-center">
                {createdCode}
              </code>
              <Button
                variant="outline"
                size="icon"
                onClick={copyToClipboard}
                className="shrink-0"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        <div className="pt-4 border-t space-y-2 text-xs text-muted-foreground">
          <p><strong>💡 Tips:</strong> Test rabatten i Shopify checkout etter oppretting.</p>
          <p>Hvis rabatten ikke fungerer, dobbeltsjekk at alle GID-er matcher produkt-IDene i Shopify Admin.</p>
        </div>
      </CardContent>
    </Card>
  );
};
